import * as React from 'react';
import { InvoiceLine } from './InvoiceLine';
import { SubtotalLine } from './SubtotalLine';

import { InvoiceTextLine } from './TextLine';
import { InvoiceTitleLine } from './TitleLine';
import { InvoiceItemType } from '../../Interfaces/Draft/DraftItemType';
import { LineInterface, LineOrder } from '../../Interfaces/Draft/LineInterface';
import { SettingsInterface } from '../../Interfaces/Settings/SettingsInterface';
import { Type } from '../../Other/Type';
import { l, tsxl } from '../../Other/Translation';
import { DraftFieldsInterface } from '../../Interfaces/Draft/FieldsInterface';
import Immutable from '../../Other/Immutable';
import { NumberFormatter } from '../../Other/Format/NumberFormatter';
import { Format } from '../../Other/Format/Format';
import { PercentageFormatter } from '../../Other/Format/PercentageFormatter';

export interface InvoiceLinesInterface {
    lines?: InvoiceItemType[];
}

export interface InvoiceLinesPropsInterface extends InvoiceLinesInterface {
    columns?: Array<keyof LineInterface>;
    settings?: SettingsInterface;
    onChange: (lines: InvoiceItemType[]) => void;
    fields?: DraftFieldsInterface;
    locale: string;
    readOnly?: boolean;
    products?: any[];
    currencySymbol: string;
    openProductModal: (index) => void;
    openVATOptions: () => void;
    openUnitOptions: () => void;
}

export class InvoiceItems extends React.Component<InvoiceLinesPropsInterface, InvoiceLinesInterface> {
    constructor(props) {
        super(props);

        this.lines = Immutable.copy(props.lines);
        this.setFormatters(props.currencySymbol);
    }

    private lines: InvoiceItemType[];
    private discountFormatter: NumberFormatter;
    private currencyFormatter: NumberFormatter;

    // @ts-ignore
    public static defaultProps: InvoiceLinesPropsInterface = {
        lines: [],
        onChange: () => {
        },
        locale: 'en_GB'
    };

    private dropLine(at, which): void {
        const b = this.lines.splice(which, 1);
        if (at >= this.lines.length) {
            this.lines.push(b[0]);
        } else {
            const a = this.lines.splice(at, 1, b[0]);
            this.lines.splice(at + 1, 0, a[0]);
        }
    }

    private triggerChange(): void {
        this.props.onChange(this.lines);
    }

    public componentWillReceiveProps(props) {
        this.setFormatters(props.currencySymbol);
    }

    private setFormatters(currency) {
        if (this.props.fields.discountType.value === 'amount') {
            this.discountFormatter = new NumberFormatter(Format.fromLocale(this.props.locale), currency);
        } else {
            this.discountFormatter = new PercentageFormatter(Format.fromLocale(this.props.locale), true);
        }
        this.currencyFormatter = new NumberFormatter(Format.fromLocale(this.props.locale), currency);
    }

    private columnsLength() {
        return this.props.columns.length - (~this.props.columns.indexOf('index') ? 1 : 0);
    }

    public render(): JSX.Element {
        const self = this;
        self.lines = Immutable.copy(this.props.lines);
        const lines: JSX.Element[] = [];
        this.props.lines.forEach((line, key) => {
            if (Type.isInvoiceLine(line)) {
                lines.push(
                    <InvoiceLine columns={this.props.columns} settings={this.props.settings} id={'' + key}
                        index={key}
                        automation={'invoiceItem-' + key} key={key} {...line}
                        currencyFormat={this.currencyFormatter}
                        locale={this.props.locale}
                        onDrop={(id) => {
                            self.dropLine(key, id);
                            self.triggerChange();
                        }}
                        onDelete={() => {
                            self.lines.splice(key, 1);
                            self.triggerChange();
                        }}
                        onChange={(value) => {
                            this.lines[key] = value;
                            this.triggerChange();
                        }}

                        readOnly={this.props.readOnly}
                    />
                );
            } else if (Type.isInvoiceSubtotalLine(line)) {
                lines.push(
                    <SubtotalLine
                        currencyFormat={this.currencyFormatter}
                        automation={`invoiceItem-Subtotal-${key}`}
                        grossTotal={line.grossTotal}
                        taxTotal={line.taxTotal}
                        netTotal={line.netTotal}
                        showNetTotal={this.props.columns.indexOf('netTotal') >= 0}
                        showTaxTotal={this.props.columns.indexOf('taxTotal') >= 0}
                        showGrossTotal={this.props.columns.indexOf('grossTotal') >= 0}
                        type="subtotal"
                        columns={this.columnsLength()}
                        title={l('invoice.subtotalTitle')}
                        id={key}
                        key={key}
                        onDelete={() => {
                            self.lines.splice(key, 1);
                            self.triggerChange();
                        }}
                        onDrop={(id) => {
                            self.dropLine(key, id);
                            self.triggerChange();
                        }}
                        readonly={this.props.readOnly}
                    />
                );
            } else if (Type.isInvoiceTitleLine(line)) {
                lines.push(
                    <InvoiceTitleLine
                        columns={this.columnsLength()}
                        id={key}
                        automation={'invoiceItem-' + line.type + '-' + key} type={line.type} key={key}
                        content={line.content}
                        onDrop={(id) => {
                            self.dropLine(key, id);
                            self.triggerChange();
                        }}
                        onChange={(value) => {
                            self.lines[key] = value;
                            self.triggerChange();
                        }}
                        onDelete={() => {
                            self.lines.splice(key, 1);
                            self.triggerChange();
                        }}
                        readonly={this.props.readOnly}
                        placeholder={l('invoice.line.title')}
                    />
                );
            } else {
                lines.push(
                    <InvoiceTextLine
                        columns={this.columnsLength()}
                        id={key}
                        automation={'invoiceItem-' + line.type + '-' + key} type={line.type} key={key}
                        content={line.content}
                        onDrop={(id) => {
                            self.dropLine(key, id);
                            self.triggerChange();
                        }}
                        onChange={(value) => {
                            self.lines[key] = value;
                            self.triggerChange();
                        }}
                        onDelete={() => {
                            self.lines.splice(key, 1);
                            self.triggerChange();
                        }}
                        readonly={this.props.readOnly}
                    />
                );
            }
        });

        const th = [];
        let columnName;
        LineOrder.forEach((name) => {
            columnName = name;
            if (name === 'grossTotal') {
                columnName = 'total';
            }
            if (this.props.columns.indexOf(name) > -1) {
                th.push((
                    <th key={name} className={name}>{tsxl('invoice.line.' + columnName)}</th>
                ));
            }
        });

        return (
            <div>
                <hr />
                <table className="InvoiceItems">
                    <thead>
                        <tr>
                            <th className="drag"></th>
                            {th}
                            <th className="remove"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines}
                    </tbody>
                </table>

            </div>
        );
    }
}
